import React, { useState, useEffect, useCallback } from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    BlobProvider,
    Font
} from '@react-pdf/renderer';
import { Button, Box, Typography, CircularProgress, Alert, Card, CardContent, Container, Grid } from '@mui/material';
import { useFormContext } from '../context/FormContext';
import { useQuestionnaireContext } from '../context/QuestionnaireContext';
import { goalMappings } from '../utils/consts';
import { verifyPayment, trackPDFUnlockChoice } from '../utils/api';
import EmailCollectionModal from './EmailCollectionModal';
import PaymentButton from './PaymentButton';

// Register Roboto font with simplified configuration
Font.register({
    family: 'Roboto',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf'
});

// Register bold and italic fonts separately
Font.register({
    family: 'Roboto-Bold',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf'
});

Font.register({
    family: 'Roboto-Italic',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf'
});

const styles = StyleSheet.create({
    page: {
        padding: 30,
        backgroundColor: '#FFFFFF',
        fontFamily: 'Roboto'
    },
    section: {
        marginBottom: 30,
        borderBottom: '1 solid #CCCCCC',
        paddingBottom: 20
    },
    title: {
        fontSize: 24,
        marginBottom: 20,
        textAlign: 'center',
        fontFamily: 'Roboto-Bold'
    },
    introText: {
        fontSize: 12,
        marginBottom: 30,
        lineHeight: 1.5,
        textAlign: 'justify',
        fontFamily: 'Roboto'
    },
    sectionTitle: {
        fontSize: 16,
        marginBottom: 15,
        fontFamily: 'Roboto-Bold',
        color: '#000000'
    },
    explanatoryText: {
        fontSize: 12,
        marginBottom: 10,
        color: '#c79a5f',
        lineHeight: 1.5,
        fontFamily: 'Roboto-Italic'
    },
    content: {
        fontSize: 12,
        marginBottom: 10,
        lineHeight: 1.5,
        fontFamily: 'Roboto'
    },
    highlightedContent: {
        fontSize: 12,
        marginBottom: 10,
        color: '#4CAF50',
        fontFamily: 'Roboto-Bold'
    },
    bulletPoint: {
        marginLeft: 10,
        marginBottom: 5,
        fontFamily: 'Roboto'
    },
    numberedPoint: {
        marginLeft: 10,
        marginBottom: 5,
        fontFamily: 'Roboto'
    },
    firstStep: {
        fontSize: 12,
        marginTop: 10,
        marginBottom: 10,
        color: '#4CAF50',
        fontFamily: 'Roboto-Bold',
        paddingTop: 10
    },
    rankedValue: {
        fontSize: 12,
        marginBottom: 8,
        color: '#4CAF50',
        fontFamily: 'Roboto-Bold',
        marginLeft: 10
    }
});

// Helper function to format arrays as text
const formatList = (items) => {
    if (!items || !Array.isArray(items)) return '';
    return items.filter(item => item && item.trim()).map(
        (item, index) => `${item}`
    ).join('\n');
};

const PDFDocument = ({ formData }) => {
    const processGoal = goalMappings[formData.cel_wybor]?.process || formData.cel_wybor;

    const introText = `Oto Twój "Pakiet Mocy". Jest to plik, który może Ci pomóc w osiągnięciu celu, zawiera on esencję tego co dla Ciebie ważne, czym się kierujesz oraz jakie masz możliwości by osiągnąć cel jakim jest ${processGoal}. Największy efekt przyniesie gdy będzie blisko Ciebie - zawieś go na ścianie w miejscu w którym przygotowujesz się do zmiany pracy (np. nad biurkiem), noś go w kieszeni i zaglądaj do niego tak często, jak to możliwe. Niech będzie największym wsparciem i drogowskazem dla Ciebie w czasie zmiany.`;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <Text style={styles.title}>Pakiet Mocy</Text>
                <Text style={styles.introText}>{introText}</Text>

                {/* Cel Section */}
                <View>
                    <Text style={styles.sectionTitle}>Chcę osiągnąć...</Text>
                    <Text style={styles.explanatoryText}>
                        Celem jest to, co byś chciał żeby się stało, a co jest możliwe przy podjęciu określonych działań.
                        Cel to marzenie wyposażone w strategię, kolejne kroki. To konkretny punkt, który chcesz osiągnąć w określonej przyszłości.
                    </Text>
                    <Text style={styles.explanatoryText}>
                        Wyznaczone cele zwiększają motywację, ułatwiają planowanie, dają powód do działania i pomagają utrzymać zaangażowanie.
                        Dają poczucie kontroli i budują pewność siebie. Dlatego doskonale, że go jasno określiłeś.
                    </Text>
                    <Text style={styles.content}>Twoim celem jest...</Text>
                    <Text style={styles.highlightedContent}>{processGoal}.</Text>
                </View>
                <View>
                    <Text style={styles.sectionTitle}>Potrzeby</Text>
                    <Text style={styles.explanatoryText}>
                        Pamiętaj! To dzięki określeniu potrzeb będziesz wiedział czy cel, który wymyśliłeś, będzie w stanie je zaspokoić.
                        Tylko zaspokojone potrzeby dają szansę na długotrwałą satysfakcję z osiągniętego celu.
                    </Text>
                    <Text style={styles.content}>
                        Chcę aby {processGoal} zaspokajało takie potrzeby jak:
                    </Text>
                    <Text style={styles.highlightedContent}>{formatList(formData.potrzeby_wybrane)}</Text>
                </View>

                {/* Wartości Section */}
                <View>
                    <Text style={styles.sectionTitle}>Wartości</Text>
                    <Text style={styles.explanatoryText}>
                        Wartości są drugim niezbędnym elementem, którego realizacja daje szansę na długotrwałą satysfakcję.
                        Razem z potrzebami stanowią podstawę długotrwałego poczucia szczęścia z realizowanych zamierzeń.
                        Świadomość hierarchii wartości pomaga w podejmowaniu decyzji zgodnych z tym, co jest dla Ciebie najważniejsze.
                    </Text>
                    <Text style={styles.content}>Chcę aby {processGoal} realizowała wartości jak:</Text>
                    <Text style={styles.rankedValue}>{formatList([...formData.wartosci_wybrane_poczatek, ...formData.hierarchia_wartosci], false, true)}</Text>
                </View>

                {/* Zasoby Section */}
                <View>
                    <Text style={styles.sectionTitle}>Zasoby </Text>
                    <Text style={styles.content}>Co już mam?</Text>
                    <Text style={styles.explanatoryText}>
                        Przy realizacji tego celu, zasoby to wszystko, co możesz wykorzystać w {processGoal.replace('enie', 'eniu').replace('wrót', 'wrocie').replace('zja', 'zji').replace('anie', 'aniu')}. Świadomość zasobów jest drogą do efektywnego ich wykorzystania dla osiągnięcia celu.
                    </Text>
                    <Text style={styles.highlightedContent}>{formatList(formData.zasoby_lista)}</Text>
                    <Text style={styles.content}>Czego potrzebuję, a jeszcze nie mam?</Text>
                    <Text style={styles.explanatoryText}>
                        Świadomość zasobów, których Ci jeszcze brakuje, daje szansę na ich uzupełnienie.
                    </Text>
                    <Text style={styles.highlightedContent}>{formData.zasoby_brakujace}</Text>
                </View>

                {/* Wspierające Myśli Section */}
                <View>
                    <Text style={styles.sectionTitle}>Wspierające myśli</Text>
                    <Text style={styles.explanatoryText}>
                        Wyłapanie wspierających myśli ma znaczenie w utrzymaniu motywacji i radzeniu sobie z trudnościami w trakcie realizacji celu.
                        Wspierające myśli pozwalają wierzyć, że cel jest możliwy, dodają energii i motywują do działania.
                    </Text>
                    <Text style={styles.content}>Oto myśli, które wspierają mój cel:</Text>
                    <Text style={styles.highlightedContent}>{formatList(formData.wspierajace_mysli_lista)}</Text>
                </View>

                {/* Ekologia Section */}
                <View>
                    <Text style={styles.sectionTitle}>Przewidywany wpływ celu na otoczenie</Text>
                    <Text style={styles.explanatoryText}>
                        {String(processGoal).charAt(0).toUpperCase() + String(processGoal).slice(1)} będzie miało wpływ na Twoje otoczenie. Świadomość tego wpływu może być dla Ciebie dodatkową motywacją do działania.
                    </Text>
                    <Text style={styles.content}>Przewiduje, że {processGoal} będzie miał taki wpływ na moje otoczenie:</Text>
                    <Text style={styles.highlightedContent}>{formData.ekologia_codziennosc}</Text>
                    <Text style={styles.highlightedContent}>{formData.ekologia_relacje}</Text>
                    <Text style={styles.highlightedContent}>{formData.ekologia_mysli_o_sobie}</Text>
                    <Text style={styles.highlightedContent}>{formData.ekologia_samopoczucie}</Text>
                </View>

                {/* Plan Działania Section */}
                <View>
                    <Text style={styles.sectionTitle}>Plan działania</Text>
                    <Text style={styles.explanatoryText}>Plan działania i strategia są kluczowe dla skutecznej realizacji celu. Działają one jak mapa i kompas, pomagając utrzymać kurs
                        i unikać zbędnych przeszkód</Text>
                    <Text style={styles.content}>Oto kroki, które pomogą mi osiągnąć cel:</Text>
                    <Text style={styles.highlightedContent}>{formatList(formData.plan_dzialania_kroki, true)}</Text>
                    {formData.plan_dzialania_kroki?.length > 0 && (
                        <>
                            <Text style={styles.content}>Oto mój pierwszy mały krok:</Text>
                            <Text style={styles.firstStep}>{formData.plan_dzialania_kroki[0]}</Text>
                        </>
                    )}
                </View>
            </Page>
        </Document>
    );
};


// Component with retry logic for PDF generation
const PDFWithRetry = ({ formData, resetForm, fileName }) => {
    const [retryCount, setRetryCount] = useState(0);
    const [forceRefresh, setForceRefresh] = useState(0);
    const [currentError, setCurrentError] = useState(null);
    const MAX_RETRIES = 3;

    const handleRetry = useCallback(() => {
        if (retryCount < MAX_RETRIES) {
            setRetryCount(prev => prev + 1);
            setForceRefresh(prev => prev + 1);
        }
    }, [retryCount, MAX_RETRIES]);

    const handleManualRefresh = () => {
        setRetryCount(0);
        setForceRefresh(prev => prev + 1);
        setCurrentError(null);
    };

    // Auto-retry on error if we haven't exceeded max retries
    useEffect(() => {
        if (currentError && retryCount < MAX_RETRIES) {
            const timer = setTimeout(() => {
                handleRetry();
            }, 1000); // Wait 1 second before retry
            return () => clearTimeout(timer);
        }
    }, [currentError, retryCount, handleRetry, MAX_RETRIES]);

    return (
        <BlobProvider key={forceRefresh} document={<PDFDocument formData={formData} />}>
            {({ blob, url, loading, error }) => {
                // Update error state when error changes
                if (error !== currentError) {
                    setCurrentError(error);
                }

                if (loading) {
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                Generowanie PDF...
                                {retryCount > 0 && ` (próba ${retryCount + 1}/${MAX_RETRIES + 1})`}
                            </Typography>
                        </Box>
                    );
                }

                if (error) {
                    if (retryCount >= MAX_RETRIES) {
                        // Show manual retry option after max retries
                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    Wystąpił błąd podczas generowania PDF po {MAX_RETRIES} próbach.
                                </Alert>
                                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'grey.600' }}>
                                    Spróbuj odświeżyć stronę lub wygenerować PDF ponownie.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleManualRefresh}
                                        sx={{
                                            background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                            '&:hover': {
                                                background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                                transform: 'scale(1.05)'
                                            }
                                        }}
                                    >
                                        Spróbuj ponownie
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => window.location.reload()}
                                    >
                                        Odśwież stronę
                                    </Button>
                                </Box>
                            </Box>
                        );
                    }

                    // Show loading while auto-retrying
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                Ponawiam próbę generowania PDF... ({retryCount + 1}/{MAX_RETRIES})
                            </Typography>
                        </Box>
                    );
                }

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Gratulacje! Twój Pakiet Mocy jest gotowy do pobrania.
                        </Alert>
                        <Button
                            variant="contained"
                            color="primary"
                            href={url}
                            download={fileName}
                            sx={{
                                py: 2,
                                px: 4,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                    transform: 'scale(1.05)'
                                }
                            }}
                        >
                            Pobierz Pakiet Mocy
                        </Button>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => {
                                    // Track restart button click
                                    trackPDFUnlockChoice(
                                        'restart',
                                        'Zacznij od nowa',
                                        'click',
                                        {
                                            variant: 'outlined',
                                            color: 'secondary',
                                            location: 'pdf_download_section'
                                        }
                                    ).catch(error => {
                                        console.error('Failed to track PDF unlock choice:', error);
                                    });
                                    resetForm();
                                }}
                                sx={{ mt: 2 }}
                            >
                                Zacznij od nowa
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => {
                                    // Track home button click
                                    trackPDFUnlockChoice(
                                        'home',
                                        'Powrót do strony głównej',
                                        'click',
                                        {
                                            variant: 'outlined',
                                            color: 'secondary',
                                            location: 'pdf_download_section'
                                        }
                                    ).catch(error => {
                                        console.error('Failed to track PDF unlock choice:', error);
                                    });
                                    window.location.href = '/';
                                }}
                                sx={{ mt: 2 }}
                            >
                                Powrót do strony głównej
                            </Button>
                        </div>
                    </Box>
                );
            }}
        </BlobProvider>
    );
};

const PDFHeader = () => {
    return (
        <Typography textAlign="center" variant="h6" color="black">
            Pakiet Mocy to indywidualny plan. Stworzony na bazie sprawdzonych metod, ale zapisuje się tylko u Ciebie. Twoja anonimowość jest dla nas priorytetem.<br /><br />
        </Typography>
    );
};
const PDFGenerator = ({ formData }) => {
    const { resetForm } = useFormContext();
    const {
        canDownloadPDF,
        hasCompletedPostQuestionnaire,
        hasPaid,
        hasProvidedEmail,
        submitEmail,
        completePurchase
    } = useQuestionnaireContext();
    const fileName = "PDF_Mocy.pdf";
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
    const [paymentVerificationError, setPaymentVerificationError] = useState(null);

    // Check for payment status in URL parameters and verify with backend
    useEffect(() => {
        const verifyPaymentStatus = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const paymentStatus = urlParams.get('payment');
            const sessionId = urlParams.get('session_id');

            if (paymentStatus === 'success' && sessionId && !hasPaid) {
                setIsVerifyingPayment(true);
                setPaymentVerificationError(null);

                try {
                    // Verify payment with backend
                    const verification = await verifyPayment(sessionId);

                    if (verification.verified) {
                        // Payment confirmed by backend
                        completePurchase();
                        // Clean up URL
                        window.history.replaceState({}, document.title, window.location.pathname);
                    } else {
                        // Payment not yet confirmed
                        setPaymentVerificationError(
                            'Płatność nie została jeszcze potwierdzona. To może potrwać kilka sekund. Odśwież stronę za chwilę.'
                        );
                    }
                } catch (error) {
                    console.error('Payment verification error:', error);
                    setPaymentVerificationError(
                        'Nie udało się zweryfikować płatności. Jeśli dokonałeś płatności, odśwież stronę za chwilę lub skontaktuj się z nami.'
                    );
                } finally {
                    setIsVerifyingPayment(false);
                }
            }
        };

        verifyPaymentStatus();
    }, [hasPaid, completePurchase]);

    const handleEmailSubmit = async (data) => {
        await submitEmail(data);
        setShowEmailModal(false);
    };

    // Show payment verification in progress
    if (isVerifyingPayment) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 80px)',
                padding: 4
            }}>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h5" gutterBottom>
                    Weryfikowanie płatności...
                </Typography>
                <Typography variant="body1" sx={{ color: 'grey.600', textAlign: 'center', maxWidth: 500 }}>
                    Sprawdzamy status Twojej płatności w systemie Stripe. To może potrwać kilka sekund.
                </Typography>
            </Box>
        );
    }

    // Show payment verification error
    if (paymentVerificationError) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 80px)',
                padding: 4
            }}>
                <Alert severity="warning" sx={{ mb: 3, maxWidth: 600 }}>
                    {paymentVerificationError}
                </Alert>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        sx={{
                            background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                transform: 'scale(1.05)'
                            }
                        }}
                    >
                        Odśwież stronę
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setPaymentVerificationError(null);
                            window.history.replaceState({}, document.title, window.location.pathname);
                        }}
                    >
                        Powrót
                    </Button>
                </Box>
            </Box>
        );
    }

    // Show PDF download if user has access
    if (canDownloadPDF) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 80px)',
                padding: 4
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 4,

                    backgroundColor: 'rgba(250,250,250,0.5)'
                }}>
                    <Typography variant="h4" gutterBottom>
                        Twój Pakiet Mocy jest gotowy!
                    </Typography>

                    <Typography variant="body1" paragraph sx={{ maxWidth: 600, textAlign: 'center', mb: 4 }}>
                        Możesz teraz pobrać swój Pakiet Mocy. Wydrukuj go i trzymaj w widocznym miejscu,
                        aby przypominał Ci o Twoim celu, wartościach i planie działania.
                    </Typography>

                    <PDFWithRetry
                        formData={formData}
                        resetForm={resetForm}
                        fileName={fileName}
                    />
                </Box>
            </Box>
        );
    }

    // Show 3 options if user doesn't have access
    return (
        <Box sx={{ py: 8, bgcolor: 'grey.50', minHeight: 'calc(100vh - 80px)' }}>
            <Container maxWidth="xl">

                <Typography
                    variant="h3"
                    sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'grey.800',
                        mb: 6
                    }}
                >
                    By uzyskać Twój plan działania - PAKIET MOCY wybierz jedną z opcji:
                </Typography>
                <PDFHeader />
                <Grid container spacing={4} justifyContent="center">
                    {/* Option 1: Complete Post Questionnaire */}
                    <Grid item xs={12} sm={6} md={4} lg={3.5}>
                        <Card sx={{
                            height: '100%',
                            p: 3,
                            textAlign: 'center'
                        }}>
                            <CardContent>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'grey.800' }}>
                                    Wypełnij ankietę
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'grey.600', mb: 3, lineHeight: 1.6 }}>
                                    Jesteś w centrum projektu Find solution! Pomóż nam go dopracować. Po przetestowaniu, podziel się opinią. Twoje uwagi pozwolą nam stworzyć lepsze narzędzie wspierające dobrostan i motywację.
                                </Typography>
                                <Button
                                    variant={hasCompletedPostQuestionnaire ? "contained" : "outlined"}
                                    disabled={hasCompletedPostQuestionnaire}
                                    onClick={() => {
                                        // Track questionnaire choice with button details
                                        trackPDFUnlockChoice(
                                            'questionnaire',
                                            hasCompletedPostQuestionnaire ? 'Gotowe ✓' : 'Wypełnij Ankietę',
                                            'click',
                                            {
                                                disabled: hasCompletedPostQuestionnaire,
                                                variant: hasCompletedPostQuestionnaire ? 'contained' : 'outlined'
                                            }
                                        ).catch(error => {
                                            console.error('Failed to track PDF unlock choice:', error);
                                        });
                                        window.location.href = '/ankieta-koncowa';
                                    }}
                                    sx={{
                                        borderColor: 'primary.main',
                                        color: hasCompletedPostQuestionnaire ? 'white' : 'primary.main',
                                        background: hasCompletedPostQuestionnaire ? '#27ae60' : 'transparent',
                                        '&:hover': !hasCompletedPostQuestionnaire ? {
                                            bgcolor: 'grey.100',
                                            borderColor: 'secondary.main'
                                        } : {},
                                        '&:disabled': {
                                            background: '#27ae60',
                                            color: 'white'
                                        }
                                    }}
                                >
                                    {hasCompletedPostQuestionnaire ? 'Gotowe ✓' : 'Wypełnij Ankietę'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Option 2: Payment - Highlighted */}
                    <Grid item xs={12} sm={6} md={4} lg={3.5}>
                        <Card sx={{
                            height: '100%',
                            p: 3,
                            textAlign: 'center',
                            position: 'relative'
                        }}>

                            <CardContent sx={{ pt: 4 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: 'grey.800' }}>
                                    Wpłać i bądź pierwszy
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
                                    10 zł
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'grey.600', mb: 3, lineHeight: 1.6 }}>
                                    Wykup dostęp do pełnej wersji aplikacji już dziś za jedyne 10 zł. To symboliczny wkład w rozwój projektu, który nagrodzimy 20% rabatem na subskrypcję po jej oficjalnym starcie.
                                </Typography>
                                {hasPaid ? (
                                    <Button
                                        variant="contained"
                                        disabled
                                        sx={{
                                            background: '#27ae60',
                                            color: 'white',
                                            px: 3,
                                            py: 1.5,
                                            fontWeight: 'bold',
                                            '&:disabled': {
                                                background: '#27ae60',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Gotowe ✓
                                    </Button>
                                ) : (
                                    <PaymentButton
                                        amount={1000}
                                        description="Find solution - Dostęp do PDF"
                                        buttonText="Wspieraj rozwój"
                                        onSuccess={() => {
                                            // Track successful payment
                                            trackPDFUnlockChoice(
                                                'payment',
                                                'Wspieraj rozwój',
                                                'success',
                                                {
                                                    amount: 1000,
                                                    currency: 'pln',
                                                    description: 'Find solution - Dostęp do PDF'
                                                }
                                            ).catch(error => {
                                                console.error('Failed to track PDF unlock choice:', error);
                                            });
                                            completePurchase();
                                            alert('Dziękujemy za płatność! Możesz teraz pobrać swój Pakiet Mocy.');
                                        }}
                                        onError={(error) => {
                                            // Track payment error
                                            trackPDFUnlockChoice(
                                                'payment',
                                                'Wspieraj rozwój',
                                                'error',
                                                {
                                                    error: error.message || 'Payment failed',
                                                    amount: 1000,
                                                    currency: 'pln'
                                                }
                                            ).catch(trackError => {
                                                console.error('Failed to track PDF unlock choice:', trackError);
                                            });
                                            console.error('Payment failed:', error);
                                            alert('Wystąpił błąd podczas płatności. Spróbuj ponownie.');
                                        }}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Option 3: Provide Email */}
                    <Grid item xs={12} sm={6} md={4} lg={3.5}>
                        <Card sx={{
                            height: '100%',
                            p: 3,
                            textAlign: 'center'
                        }}>
                            <CardContent>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'grey.800' }}>
                                    Zapisz się na powiadomienie
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'grey.600', mb: 3, lineHeight: 1.6 }}>
                                    Zapisz się by dostać powiadomienie o pełnej wersji aplikacji i uzyskaniu 10% rabatu na subskrypcję.
                                </Typography>
                                <Button
                                    fullWidth
                                    variant={hasProvidedEmail ? "contained" : "outlined"}
                                    disabled={hasProvidedEmail}
                                    onClick={() => {
                                        // Track email choice with button details
                                        trackPDFUnlockChoice(
                                            'email',
                                            hasProvidedEmail ? 'Gotowe ✓' : 'Zapisz się',
                                            'click',
                                            {
                                                disabled: hasProvidedEmail,
                                                variant: hasProvidedEmail ? 'contained' : 'outlined',
                                                fullWidth: true
                                            }
                                        ).catch(error => {
                                            console.error('Failed to track PDF unlock choice:', error);
                                        });
                                        setShowEmailModal(true);
                                    }}
                                    sx={{
                                        borderColor: 'primary.main',
                                        color: hasProvidedEmail ? 'white' : 'primary.main',
                                        background: hasProvidedEmail ? '#27ae60' : 'transparent',
                                        '&:hover': !hasProvidedEmail ? {
                                            borderColor: 'secondary.main',
                                            bgcolor: 'grey.100',
                                            transform: 'scale(1.05)'
                                        } : {},
                                        '&:disabled': {
                                            background: '#27ae60',
                                            color: 'white'
                                        }
                                    }}
                                >
                                    {hasProvidedEmail ? 'Gotowe ✓' : 'Zapisz się'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>


            <EmailCollectionModal
                open={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                onSubmit={handleEmailSubmit}
                title="Zapisz się na powiadomienie"
                description="Podaj swoje dane, a poinformujemy Cię o premierze pełnej wersji Find solution. Otrzymasz także 10% rabatu na subskrypcję!"
            />
        </Box>
    );
};

export default PDFGenerator;